import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Smartphone, Loader2, AlertCircle, ShieldCheck, Clock, CheckCircle2 } from 'lucide-react';
import * as customerApi from '../../../../services/api/customerApi';
import { format } from 'date-fns';

const DIAL_CODES = [
    { code: '+971', label: '🇦🇪 +971', ccode: 'AE' },
    { code: '+966', label: '🇸🇦 +966', ccode: 'SA' },
    { code: '+974', label: '🇶🇦 +974', ccode: 'QA' },
    { code: '+965', label: '🇰🇼 +965', ccode: 'KW' },
    { code: '+973', label: '🇧🇭 +973', ccode: 'BH' },
    { code: '+968', label: '🇴🇲 +968', ccode: 'OM' },
    { code: '+91',  label: '🇮🇳 +91',  ccode: 'IN' }
];

const getDialCodeForCountry = (countriesList, countryId) => {
    if (!countriesList || !countryId) return '+971';
    const country = countriesList.find(c => c.publicId === countryId);
    if (!country) return '+971';
    const dialCodeObj = DIAL_CODES.find(dc => dc.ccode === country.code);
    return dialCodeObj ? dialCodeObj.code : '+971';
};

const CustomerAccessTab = ({ customerId, customerCountryId, countries = [] }) => {
    const [accessList, setAccessList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Form state for new access
    const [showAddForm, setShowAddForm] = useState(false);
    const [countryDialCode, setCountryDialCode] = useState('+971');
    const [localMobile, setLocalMobile] = useState('');
    const [adding, setAdding] = useState(false);

    const loadAccessList = useCallback(async () => {
        if (!customerId) return;
        try {
            setLoading(true);
            const data = await customerApi.getCustomerAccess(customerId);
            setAccessList(data || []);
            setError(null);
        } catch (err) {
            console.error('[CustomerAccessTab] Failed to load access list:', err);
            setError('Failed to load authorized devices.');
        } finally {
            setLoading(false);
        }
    }, [customerId]);

    useEffect(() => {
        loadAccessList();
    }, [loadAccessList]);

    useEffect(() => {
        if (showAddForm) {
            setCountryDialCode(getDialCodeForCountry(countries, customerCountryId));
        }
    }, [showAddForm, customerCountryId, countries]);

    const handleAddAccess = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        console.log('[CustomerAccessTab] handleAddAccess initiated');
        console.log('[CustomerAccessTab] customerId:', customerId);
        console.log('[CustomerAccessTab] mobile:', localMobile);
        
        if (!localMobile || !customerId) {
            console.warn('[CustomerAccessTab] Missing data, aborting');
            return;
        }

        const cleanMobile = localMobile.replace(/^0+/, '').replace(/\s+/g, '');
        const fullMobile = countryDialCode + cleanMobile;

        setAdding(true);
        try {
            console.log('[CustomerAccessTab] Calling API with:', fullMobile);
            await customerApi.addCustomerAccess(customerId, {
                mobileNumber: fullMobile
            });
            console.log('[CustomerAccessTab] API call success');
            setLocalMobile('');
            setShowAddForm(false);
            await loadAccessList();
        } catch (err) {
            console.error('[CustomerAccessTab] Failed to add access:', err);
            const msg = err.response?.data?.message || 'Failed to authorize device. Check plan limits.';
            alert(msg);
        } finally {
            setAdding(false);
        }
    };

    const handleRemoveAccess = async (accessPublicId) => {
        if (!window.confirm('Are you sure you want to revoke access for this device?')) return;

        try {
            await customerApi.removeCustomerAccess(customerId, accessPublicId);
            await loadAccessList();
        } catch (err) {
            console.error('Failed to remove access:', err);
            alert('Failed to revoke access.');
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-cyan-600 animate-spin mb-3" />
                <p className="text-slate-500 text-sm font-medium">Loading authorization data...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header Info */}
            <div className="bg-cyan-50 border border-cyan-100 rounded-2xl p-5 flex gap-4">
                <div className="bg-white p-3 rounded-xl shadow-sm self-start flex-shrink-0">
                    <ShieldCheck className="w-6 h-6 text-cyan-600" />
                </div>
                <div>
                    <h4 className="text-slate-800 font-bold text-sm">Portal Access Management</h4>
                    <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                        Authorize mobile numbers here. The customer will be prompted to set their secure 4-digit PIN the first time they log into the Customer Ordering Portal.
                    </p>
                </div>
            </div>

            {/* List of Authorized Devices */}
            <div className="space-y-3">
                <div className="flex items-center justify-between px-1">
                    <h5 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Authorized Numbers ({accessList.length})</h5>
                    {!showAddForm && (
                        <button 
                            onClick={() => setShowAddForm(true)}
                            className="text-xs font-bold text-cyan-600 hover:text-cyan-700 flex items-center gap-1.5 transition-colors bg-cyan-50 px-3 py-1.5 rounded-lg"
                        >
                            <Plus size={14} />
                            Add Mobile Number
                        </button>
                    )}
                </div>

                {showAddForm && (
                    <div className="bg-slate-50 border-2 border-dashed border-slate-200 rounded-2xl p-5 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1.5 ml-1">Mobile Number</label>
                            <div className="flex gap-2">
                                <select 
                                    value={countryDialCode}
                                    onChange={(e) => setCountryDialCode(e.target.value)}
                                    className="w-32 px-3 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none transition-all font-medium text-slate-700"
                                >
                                    {DIAL_CODES.map(dc => (
                                        <option key={dc.code} value={dc.code}>{dc.label}</option>
                                    ))}
                                </select>
                                <div className="relative group flex-1">
                                    <Smartphone className="absolute left-3 top-2.5 w-4 h-4 text-slate-400 group-focus-within:text-cyan-600 transition-colors" />
                                    <input 
                                        type="tel"
                                        value={localMobile}
                                        onChange={(e) => setLocalMobile(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleAddAccess();
                                            }
                                        }}
                                        placeholder="50 123 4567"
                                        className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 outline-none transition-all font-medium text-slate-900"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-3 mt-5 pt-4 border-t border-slate-100">
                            <button 
                                type="button"
                                onClick={() => setShowAddForm(false)}
                                className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-800 bg-white border border-slate-200 rounded-xl shadow-sm transition-all"
                            >
                                Cancel
                            </button>
                            <button 
                                type="button"
                                onClick={handleAddAccess}
                                disabled={adding}
                                className="px-5 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold shadow-lg shadow-slate-900/10 hover:bg-slate-800 disabled:opacity-50 flex items-center gap-2 transition-all active:scale-95"
                            >
                                {adding ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
                                Authorize Number
                            </button>
                        </div>
                    </div>
                )}

                {accessList.length === 0 && !showAddForm && (
                    <div className="text-center py-10 bg-slate-50 border border-dashed border-slate-200 rounded-2xl">
                        <Smartphone className="w-10 h-10 text-slate-300 mx-auto mb-3 opacity-50" />
                        <p className="text-slate-500 text-sm font-medium">No authorized numbers found</p>
                        <p className="text-slate-400 text-[10px] mt-1">Add a mobile number to enable portal access for this customer.</p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {accessList.map((access) => (
                        <div 
                            key={access.publicId}
                            className="bg-white border border-slate-100 rounded-2xl p-4 flex flex-col group hover:border-cyan-200 hover:shadow-md hover:shadow-cyan-500/5 transition-all"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="bg-slate-50 p-2.5 rounded-xl group-hover:bg-cyan-50 group-hover:text-cyan-600 transition-colors">
                                        <Smartphone size={18} />
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-slate-800 tracking-tight">{access.mobileNumber}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => handleRemoveAccess(access.publicId)}
                                    className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                    title="Revoke Access"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>

                            <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                                <div className="flex items-center gap-1.5">
                                    {access.hasPin ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[9px] font-bold uppercase tracking-wider">
                                            <CheckCircle2 size={10} /> PIN Set
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-[9px] font-bold uppercase tracking-wider">
                                            <AlertCircle size={10} /> Pending Setup
                                        </span>
                                    )}
                                </div>
                                <p className="text-[10px] font-bold text-slate-400 flex items-center gap-1">
                                    <Clock size={10} />
                                    {access.lastLoginAt ? format(new Date(access.lastLoginAt), 'MMM d, h:mm a') : 'Never Logged In'}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Help Note */}
            <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-slate-500 leading-relaxed">
                    <strong>Note:</strong> PINs are managed securely by the customer. If a customer forgets their PIN, they can reset it via the portal. If you need to revoke their access, simply remove the number from this list.
                </p>
            </div>
        </div>
    );
};

export default CustomerAccessTab;
